import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, ChevronDown, ChevronUp, Crown } from "lucide-react";
import { api } from "../lib/api";
import { useT } from "../context/LanguageContext";

/**
 * Public overview of every running league with a compact top-3 standings
 * preview. Collapsed by default so it doesn't crowd the marketing tiles on
 * the home page. Each league card links through to its full standings page.
 */
export default function ActiveLeagues({ defaultOpen = false }) {
  const { t } = useT();
  const [open, setOpen] = useState(defaultOpen);
  const [leagues, setLeagues] = useState(null);

  useEffect(() => {
    if (!open || leagues !== null) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/leagues-active/overview?top=3");
        if (alive) setLeagues(Array.isArray(data) ? data : []);
      } catch {
        if (alive) setLeagues([]);
      }
    })();
    return () => { alive = false; };
  }, [open, leagues]);

  return (
    <section className="space-y-3" data-testid="active-leagues">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="active-leagues-toggle"
        aria-expanded={open}
        className="w-full bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-l-2 border-l-[#34C759] px-4 py-3 flex items-center gap-3 hover:border-[#34C759] transition-colors"
      >
        <Trophy className="w-4 h-4 text-[#34C759] flex-shrink-0" />
        <div className="flex-1 text-left min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#34C759]">{t("active.kicker")}</div>
          <div className="font-heading text-lg uppercase tracking-wider text-[#09090B] dark:text-[#F4F4F5] truncate">{t("active.title")}</div>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">
          {open ? t("active.hide") : t("active.toggle")}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-[#71717A]" /> : <ChevronDown className="w-4 h-4 text-[#71717A]" />}
      </button>

      {open && (
        <div className="space-y-3" data-testid="active-leagues-panel">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">{t("active.subtitle")}</p>

          {leagues === null ? (
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] py-6 text-center">LOADING…</div>
          ) : leagues.length === 0 ? (
            <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-5 font-mono text-xs text-[#71717A]" data-testid="active-leagues-empty">
              {t("active.empty")}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="active-leagues-list">
              {leagues.map((lg) => <LeagueCard key={lg.id} lg={lg} />)}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function LeagueCard({ lg }) {
  const { t } = useT();
  const platformKey = lg.platform === "console" ? "active.platform.console" : "active.platform.mobile";
  const statusKey = `active.status.${lg.status}`;
  const accent = lg.platform === "console" ? "#7C3AED" : "#EA580C";
  return (
    <Link
      to={`/leagues/${lg.id}`}
      data-testid={`active-league-${lg.id}`}
      className="block bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-t-2 px-4 py-4 transition-colors hover:border-[#7C3AED] hover:shadow-[0_0_24px_rgba(124,58,237,0.15)]"
      style={{ borderTopColor: accent }}
    >
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="font-heading text-base uppercase tracking-wider text-[#09090B] dark:text-[#F4F4F5] truncate">{lg.name}</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mt-0.5">
            {t(platformKey)} · {lg.player_count}/{lg.capacity}
          </div>
        </div>
        <span
          className="inline-flex items-center font-mono text-[10px] uppercase tracking-[0.3em] px-2 py-0.5 border flex-shrink-0"
          style={{ color: accent, borderColor: accent }}
        >
          {t(statusKey)}
        </span>
      </div>

      {lg.top?.length ? (
        <ol className="space-y-1.5" data-testid={`active-league-top-${lg.id}`}>
          {lg.top.map((row) => {
            const isFirst = row.rank === 1;
            return (
              <li key={row.user_id} className="flex items-center gap-2 text-xs font-mono">
                <span
                  className={`inline-flex items-center justify-center w-5 h-5 text-[10px] font-heading flex-shrink-0 ${
                    isFirst ? "bg-[#F59E0B] text-white shadow-[0_0_8px_rgba(245,158,11,0.4)]"
                    : row.rank === 2 ? "bg-[#94A3B8] text-white"
                    : row.rank === 3 ? "bg-[#B45309] text-white"
                    : "border border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A]"
                  }`}
                >
                  {row.rank}
                </span>
                {isFirst && <Crown className="w-3 h-3 text-[#F59E0B] flex-shrink-0" />}
                <span className="truncate flex-1 text-[#09090B] dark:text-[#F4F4F5]">{row.username}</span>
                <span className="text-[#71717A]">{row.played} {t("st.played")}</span>
                <span className="font-heading text-sm text-[#09090B] dark:text-[#F4F4F5] ml-1">{row.points}</span>
              </li>
            );
          })}
        </ol>
      ) : (
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] py-2">{t("active.noResults")}</div>
      )}

      <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#7C3AED]">
        {t("active.viewStandings")}
      </div>
    </Link>
  );
}
